import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, updateDoc, deleteDoc } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function Advances() {
  // 🔥 Context se Role
  const { advances, loading, isAdmin, isAccount, currentUser } = useWebData();

  // 🔥 Filter States (New Logic)
  const [viewMode, setViewMode] = useState('Month'); // 'Month', 'Year', 'All'
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Dynamic Year
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);

  // ==========================================
  // 1. UNIVERSAL FILTER LOGIC (Supports All Formats)
  // ==========================================
  useEffect(() => {
    if (!advances) return;

    let result = [...advances];

    // A. Search Filter (Date included)
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(item => {
            const dateStr = item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
            }).toLowerCase() : "";

            return (
                (item.userName || "").toLowerCase().includes(lower) ||
                (item.reason || "").toLowerCase().includes(lower) ||
                (item.status || "").toLowerCase().includes(lower) ||
                (item.amount || "").toString().includes(lower) ||
                dateStr.includes(lower) // 🔥 Date Search (e.g. "12 Dec")
            );
        });
    }

    // 🔥 B. Date Filter (Fixed String Matching Logic)
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); // 0-11

        result = result.filter(item => {
            const rawDate = item.createdAt || ""; 
            if (!rawDate) return false;

            let y = 0;
            let m = -1;

            // Logic 1: Try Standard Date Object (ISO Format usually)
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) {
                y = d.getFullYear();
                m = d.getMonth();
            } 
            // Logic 2: Manual Parse (Fallback)
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    // YYYY-MM-DD or DD-MM-YYYY detection
                    if (parts[2].length === 4) { 
                        y = parseInt(parts[2]);
                        m = parseInt(parts[1]) - 1;
                    } else if (parts[0].length === 4) { 
                        y = parseInt(parts[0]);
                        m = parseInt(parts[1]) - 1;
                    }
                }
            }

            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            
            return true;
        });
    }

    // C. Sort (Newest First)
    result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    setFiltered(result);
  }, [advances, searchTerm, viewMode, selectedDate, selectedYear]);

  // ==========================================
  // 2. EXCEL DOWNLOAD (All Data)
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(i => ({
        Date: formatDate(i.createdAt),
        Employee: i.userName,
        Amount: i.amount,
        Reason: i.reason || "-",
        Status: i.status,
        Approved_By: i.approvedBy || "-",
        Approved_At: formatDate(i.approvedAt)
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Advance_Report");
    XLSX.writeFile(wb, `Advance_Report_${viewMode}.xlsx`);
  };

  // --- ACTIONS ---
  const updateStatus = async (id, newStatus) => {
      if(window.confirm(`Mark request as ${newStatus}?`)) {
          try {
            await updateDoc(doc(db, "advances", id), { 
                status: newStatus,
                approvedBy: currentUser?.name || "Admin",
                approvedAt: new Date().toISOString()
            });
            if(viewData) setViewData(null); 
          } catch(e) { alert("Error updating"); }
      }
  };

  const handleSaveEdit = async () => {
      if(!editData) return;
      try {
          await updateDoc(doc(db, "advances", editData.id), {
              amount: editData.amount,
              reason: editData.reason,
              status: editData.status
          });
          alert("Advance Updated ✅");
          setEditData(null);
      } catch (e) { alert("Error updating"); }
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete this request?")) await deleteDoc(doc(db, "advances", id));
  };

  // Helper: Month/Year Logic
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  // 🔥 Dynamic Years (2024 -> Future)
  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (iso) => {
    if(!iso) return "-";
    const date = new Date(iso);
    return isNaN(date.getTime()) ? iso : date.toLocaleDateString('en-GB');
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* --- HEADER & FILTERS --- */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#e74c3c", display:"flex", alignItems:"center"}}>
                💰 Advance Manager <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length} Records)</span>
            </h2>
            <button onClick={downloadExcel} style={btnDownload}>📥 Download Excel</button>
        </div>

        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            
            {/* VIEW MODE TOGGLES */}
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {/* DATE CHANGER (Month Mode) */}
            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {/* YEAR DROPDOWN (Year Mode) */}
            {viewMode === 'Year' && (
                <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}
                >
                    {getYearOptions().map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            )}

            {/* SUPER SEARCH */}
            <input type="text" placeholder="🔍 Search Date, Name, Status..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#e74c3c", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Employee</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Reason</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="6" style={{padding:"20px", textAlign:"center", color:"#999"}}>No records found.</td></tr>
              ) : filtered.map(row => (
                <tr key={row.id} style={{borderBottom:"1px solid #eee", height:"45px"}}>
                    <td style={tdStyle}>{formatDate(row.createdAt)}</td>
                    <td style={{...tdStyle, fontWeight:"bold", color:"#2c3e50"}}>{row.userName}</td>
                    <td style={{...tdStyle, fontWeight:"bold", color:"#e74c3c"}}>₹{row.amount}</td>
                    <td style={{...tdStyle, maxWidth:"200px", color:"#555"}}>{row.reason}</td>
                    <td style={tdStyle}>
                        <span style={{
                            padding:"4px 10px", borderRadius:"12px", fontSize:"11px", fontWeight:"bold",
                            backgroundColor: row.status==='Approved'?'#e8f5e9':(row.status==='Rejected'?'#ffebee':'#fff3e0'),
                            color: row.status==='Approved'?'green':(row.status==='Rejected'?'red':'orange')
                        }}>
                            {row.status}
                        </span>
                    </td>
                    <td style={tdStyle}>
                        <div style={{display:"flex", gap:"5px"}}>
                            {/* APPROVE/REJECT (Admin/Account Only) */}
                            {row.status === 'Pending' && (isAdmin || isAccount) && (
                                <>
                                    <button onClick={() => updateStatus(row.id, 'Approved')} style={btnIconCheck} title="Approve">✔</button>
                                    <button onClick={() => updateStatus(row.id, 'Rejected')} style={btnIconCross} title="Reject">✖</button>
                                </>
                            )}
                            {/* VIEW */}
                            <button onClick={() => setViewData(row)} style={btnIconEye} title="View">👁️</button>
                            {/* EDIT/DELETE (Admin/Account Only) */}
                            {(isAdmin || isAccount) && (
                                <>
                                    <button onClick={() => setEditData(row)} style={btnIconEdit} title="Edit">✏️</button>
                                    <button onClick={() => handleDelete(row.id)} style={btnIconDelete} title="Delete">🗑️</button>
                                </>
                            )}
                        </div>
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#e74c3c", marginTop:0, borderBottom:"1px solid #eee", paddingBottom:"10px"}}>💰 Advance Details</h3>
            <div style={{fontSize:"13px", lineHeight:"1.8"}}>
                <p><strong>📅 Date:</strong> {formatDate(viewData.createdAt)}</p>
                <p><strong>👤 Employee:</strong> {viewData.userName}</p>
                <p><strong>💸 Amount:</strong> <span style={{fontSize:"16px", fontWeight:"bold"}}>₹{viewData.amount}</span></p>
                <p><strong>📝 Reason:</strong> {viewData.reason}</p>
                <p><strong>🔥 Status:</strong> {viewData.status}</p>
            </div>
            
            {viewData.status === 'Pending' && (isAdmin || isAccount) && (
                <div style={{marginTop:"15px", display:"flex", gap:"10px"}}>
                    <button onClick={()=>updateStatus(viewData.id, 'Approved')} style={btnBigApprove}>Approve</button>
                    <button onClick={()=>updateStatus(viewData.id, 'Rejected')} style={btnBigReject}>Reject</button>
                </div>
            )}

            <button onClick={() => setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#f39c12", marginTop:0}}>✏️ Edit Advance</h3>
            <label style={labelStyle}>Amount (₹)</label>
            <input type="number" value={editData.amount} onChange={(e)=>setEditData({...editData, amount:e.target.value})} style={modalInput}/>
            
            <label style={labelStyle}>Reason</label>
            <input type="text" value={editData.reason} onChange={(e)=>setEditData({...editData, reason:e.target.value})} style={modalInput}/>

            <label style={labelStyle}>Status</label>
            <select value={editData.status} onChange={(e)=>setEditData({...editData, status:e.target.value})} style={modalInput}>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved ✅</option>
                <option value="Rejected">Rejected ❌</option>
            </select>

            <div style={{marginTop:"15px", textAlign:"right"}}>
                <button onClick={()=>setEditData(null)} style={btnCancel}>Cancel</button>
                <button onClick={handleSaveEdit} style={btnSave}>Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};
const btnIconCheck={backgroundColor:"#e8f5e9", color:"green", border:"1px solid #c8e6c9", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconCross={backgroundColor:"#ffebee", color:"red", border:"1px solid #ffcdd2", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEye={backgroundColor:"#e3f2fd", color:"#1976d2", border:"1px solid #bbdefb", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEdit={backgroundColor:"#fff3e0", color:"#e67e22", border:"1px solid #ffe0b2", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};
const btnBigApprove={flex:1, padding:"12px", backgroundColor:"#27ae60", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontWeight:"bold"};
const btnBigReject={flex:1, padding:"12px", backgroundColor:"#c0392b", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontWeight:"bold"};
const btnSave={backgroundColor:"#e74c3c", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};
const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"350px", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};
const modalInput={width:"100%", padding:"8px", margin:"5px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555"};

export default Advances;